import React, { useState } from 'react';
import Tappable from 'react-tappable/lib/Tappable';
import styles from './WalletBalanceSection.module.scss';

const GROUP_THEMES = [styles.theme0, styles.theme1, styles.theme2];

const UNLIMITED_AMOUNT = 10000000;

const BADGE_COLORS = {
  FAMILY_SHARED: '#714FFF',
  DEPENDENTS_SHARED: '#4857A5',
  INDIVIDUAL_CAPPED: '#292929',
};

function themeFor(index) {
  return GROUP_THEMES[index % GROUP_THEMES.length];
}

function isUnlimited(wallet) {
  return (
    wallet.isUnlimitedTitle || Number(wallet.totalBalance) === UNLIMITED_AMOUNT
  );
}

// Family view shows the pool shared across members, not the member's own balance.
function groupBalance(wallet, familyView) {
  const shared = familyView && wallet.sharedWalletBalance;
  if (!shared) {
    return {
      unlimited: isUnlimited(wallet),
      availableText: wallet.availableBalanceText,
      totalText: wallet.totalBalanceText,
    };
  }
  return {
    unlimited: Number(shared.total) === UNLIMITED_AMOUNT,
    availableText: shared.availableText,
    totalText: shared.totalText,
  };
}

// Family view lists the whole family's spends; individual lists the member's own.
function groupSpends(wallet, familyView) {
  return familyView
    ? { services: wallet.servicesCovered || [], totalSpent: wallet.totalSpent }
    : {
        services: wallet.memberServicesCovered || [],
        totalSpent: wallet.memberTotalSpent,
      };
}

function badgeColor(primary) {
  return BADGE_COLORS[primary.walletProgramType] || BADGE_COLORS.FAMILY_SHARED;
}

function splitWallets(walletDetails) {
  const wallets = walletDetails || [];
  const primary = wallets.find((item) => item.walletId === 0) || null;
  const groups = wallets.filter((item) => item !== primary && !item.hideWallet);
  return { primary, groups };
}

function balanceBars(primary, familyView) {
  if (familyView) {
    const shared = primary.sharedPool || primary.scopeBalance;
    // A lone bar always reads as the brand colour; coral only distinguishes
    // the shared pool when it sits under a personal cap.
    return shared
      ? [
          {
            key: 'shared',
            balance: shared,
            label: primary.newLabel,
            shared: false,
          },
        ]
      : [];
  }

  const bars = [];
  const stacked = !!(primary.personalCap && primary.sharedPool);
  if (primary.personalCap) {
    bars.push({
      key: 'personal',
      balance: primary.personalCap,
      label: stacked ? 'Personal cap' : primary.newLabel,
      shared: false,
      showTotal: stacked,
    });
  }
  if (primary.sharedPool) {
    bars.push({
      key: 'shared',
      balance: primary.sharedPool,
      label: stacked ? 'Shared family pool' : primary.newLabel,
      shared: stacked,
      showTotal: stacked,
    });
  }
  // Programs with a single balance send it as `scopeBalance` instead.
  if (bars.length === 0 && primary.scopeBalance) {
    bars.push({
      key: 'scope',
      balance: primary.scopeBalance,
      label: primary.newLabel,
      shared: false,
    });
  }
  return bars;
}

function barHeading({ label, balance, showTotal }) {
  const fallback = label || 'Available Balance';
  if (!showTotal) {
    return fallback;
  }
  const total = Number(balance.total) || 0;
  const totalText =
    balance.totalText || (total > 0 ? `₹${total.toLocaleString('en-IN')}` : '');
  return totalText ? `${fallback} • ${totalText}` : fallback;
}

function subCapLabel(wallet, services, serviceItem, serviceIndex) {
  const caps = wallet.planCaps || [];
  const byName = caps.find(
    (cap) =>
      cap.serviceName === serviceItem.serviceLabel ||
      cap.serviceName === serviceItem.label ||
      (serviceItem.serviceKey && cap.serviceKey === serviceItem.serviceKey)
  );
  const match =
    byName || (caps.length === services.length ? caps[serviceIndex] : null);
  const perEvent = match && match.perEventCap;
  return perEvent && perEvent.amount && perEvent.amount !== UNLIMITED_AMOUNT
    ? `Sub cap: ${perEvent.text}`
    : null;
}

function BalanceBar({ bar }) {
  const { balance, shared } = bar;
  // Nothing to track against an unlimited balance, so skip the progress bar.
  if (bar.unlimited || Number(balance.total) === UNLIMITED_AMOUNT) {
    return (
      <div
        className={`${styles.balanceBar} ${shared ? styles.barShared : ''}`.trim()}
      >
        <p className={styles.balanceLabel}>
          {bar.label || 'Available Balance'}
        </p>
        <p className={styles.unlimitedValue}>Unlimited</p>
      </div>
    );
  }

  const total = Number(balance.total) || 0;
  const used = Number(balance.used) || 0;
  const percent =
    total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

  return (
    <div
      className={`${styles.balanceBar} ${shared ? styles.barShared : ''}`.trim()}
    >
      <p className={styles.balanceLabel}>{barHeading(bar)}</p>
      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ '--wallet-progress': `${percent}%` }}
        />
      </div>
      <div className={styles.balanceRow}>
        <p className={styles.balanceUsed}>Used: {balance.usedText}</p>
        <p className={styles.balanceRemaining}>
          Remaining: {balance.remainingText}
        </p>
      </div>
    </div>
  );
}

function BalanceBox({ primary, familyView }) {
  let bars = balanceBars(primary, familyView);
  // Unlimited wallets may not send a pool at all; still show the balance.
  if (bars.length === 0 && isUnlimited(primary)) {
    bars = [{ key: 'unlimited', label: primary.newLabel, unlimited: true }];
  }
  if (bars.length === 0) {
    return null;
  }

  return (
    <div className={styles.balanceBox}>
      {bars.map((bar) => (
        <BalanceBar key={bar.key} bar={bar} />
      ))}
      {primary.scopeDescription && (
        <p className={styles.scopeDescription}>{primary.scopeDescription}</p>
      )}
    </div>
  );
}

function MemberSelector({ sections, activeIndex, onSelect }) {
  if (sections.length <= 1) {
    return null;
  }
  return (
    <div className={styles.memberSelector}>
      {sections.map((section, index) => {
        const selected = index === activeIndex;
        return (
          <Tappable
            key={index}
            component="div"
            className={styles.memberTap}
            onTap={() => onSelect(index)}
          >
            <div
              className={`${styles.memberPill} ${
                selected ? styles.memberPillSelected : ''
              }`}
            >
              {section.memberImageUrl && (
                <img
                  src={section.memberImageUrl}
                  alt=""
                  className={styles.memberAvatar}
                />
              )}
              <p className={styles.memberName}>
                {section.isSelf ? 'Self' : section.WalletDepName}
              </p>
            </div>
          </Tappable>
        );
      })}
    </div>
  );
}

function SpendsPanel({ wallet, spends }) {
  const { services, totalSpent } = spends;
  if (services.length === 0) {
    return null;
  }
  return (
    <div className={styles.spendsPanel}>
      <p className={styles.spendsTitle}>Spends</p>
      <div className={styles.spendsDivider} />
      {services.map((serviceItem, index) => {
        const cap = subCapLabel(wallet, services, serviceItem, index);
        return (
          <div
            key={`${serviceItem.label}-${index}`}
            className={styles.spendRow}
          >
            <div className={styles.spendLeft}>
              <div className={styles.spendService}>
                {serviceItem.icon && (
                  <img
                    src={serviceItem.icon}
                    alt=""
                    className={styles.spendIcon}
                  />
                )}
                <p className={styles.spendLabel}>
                  {serviceItem.label || serviceItem.serviceLabel}
                </p>
              </div>
              {cap && <p className={styles.subCapTag}>{cap}</p>}
            </div>
            <p className={styles.spendAmount}>₹ {serviceItem.amountSpent}</p>
          </div>
        );
      })}
      <div className={styles.totalSpends}>
        <span className={styles.totalSpendsLabel}>Total Spends</span>
        <span className={styles.totalSpendsValue}>₹ {totalSpent || 0}</span>
      </div>
    </div>
  );
}

function ServiceGroups({ groups, openGroupIndex, onToggle, familyView }) {
  if (groups.length === 0) {
    return null;
  }
  return (
    <div>
      <p className={styles.servicesHeading}>Services in this wallet</p>
      {groups.map((wallet, index) => {
        const open = openGroupIndex === index;
        const balance = groupBalance(wallet, familyView);
        const spends = groupSpends(wallet, familyView);
        const expandable = spends.services.length > 0;
        return (
          <div
            key={wallet.walletId || index}
            className={`${styles.serviceGroup} ${themeFor(index)}`}
          >
            <Tappable component="div" onTap={() => onToggle(index)}>
              <div className={styles.serviceCard}>
                <p className={styles.serviceTitle}>{wallet.newLabel}</p>
                <p className={styles.serviceAmount}>
                  {balance.unlimited ? (
                    <span className={styles.amountValue}>Unlimited</span>
                  ) : (
                    <>
                      <span className={styles.amountValue}>
                        ₹ {balance.availableText} /{' '}
                      </span>
                      <span className={styles.amountTotal}>
                        {balance.totalText}
                      </span>
                    </>
                  )}
                  {expandable && (
                    <span
                      className={`${styles.chevron} ${
                        open ? styles.chevronOpen : ''
                      }`}
                    />
                  )}
                </p>
              </div>
            </Tappable>
            {open && <SpendsPanel wallet={wallet} spends={spends} />}
          </div>
        );
      })}
    </div>
  );
}

function WalletBalanceSection({
  walletSections,
  walletProgramTagLabel,
  showFamilyTab,
}) {
  const [activeTab, setActiveTab] = useState('family');
  const [selectedMemberIndex, setSelectedMemberIndex] = useState(0);
  const [openGroupIndex, setOpenGroupIndex] = useState(null);

  const sections = walletSections || [];
  if (sections.length === 0) {
    return null;
  }

  const selectTab = (tab) => {
    setActiveTab(tab);
    setOpenGroupIndex(null);
  };

  const selectMember = (index) => {
    setSelectedMemberIndex(Number(index));
    setOpenGroupIndex(null);
  };

  const toggleGroup = (index) =>
    setOpenGroupIndex((prev) => (prev === index ? null : index));

  const familyView = showFamilyTab && activeTab === 'family';
  const selfIndex = sections.findIndex((item) => item.isSelf);
  const activeIndex = familyView
    ? selfIndex >= 0
      ? selfIndex
      : 0
    : Math.min(selectedMemberIndex, sections.length - 1);

  const { primary, groups } = splitWallets(sections[activeIndex].walletDetails);
  if (!primary) {
    return null;
  }

  // API sends the family heading for self; on the Individual tab name the member instead.
  const activeSection = sections[activeIndex];
  const individualView = showFamilyTab && activeTab === 'individual';
  let balanceHeading = primary.balanceHeading || 'Your Wallet';
  if (individualView) {
    balanceHeading = activeSection.isSelf
      ? "Self's Available Balance"
      : `${activeSection.WalletDepName}'s Available Balance`;
  }

  const memberSelector = familyView ? null : (
    <MemberSelector
      sections={sections}
      activeIndex={activeIndex}
      onSelect={selectMember}
    />
  );

  return (
    <div className={styles.section}>
      <div>
        <p className={styles.sectionTitle}>Your Wallet Balance</p>
        {walletProgramTagLabel && (
          <p className={styles.programTag}>{walletProgramTagLabel}</p>
        )}
      </div>

      {showFamilyTab && (
        <div className={styles.tabSwitch}>
          {['family', 'individual'].map((tab) => (
            <Tappable
              key={tab}
              component="div"
              className={styles.tab}
              onTap={() => selectTab(tab)}
            >
              <div
                className={`${styles.tabLabel} ${
                  activeTab === tab ? styles.tabActive : ''
                }`}
              >
                {tab}
              </div>
            </Tappable>
          ))}
        </div>
      )}

      <div
        className={`${styles.card} ${
          memberSelector ? styles.cardWithSelector : ''
        }`}
      >
        {memberSelector}

        <div className={styles.cardBody}>
          <div className={styles.cardHeader}>
            <p className={styles.walletHeading}>{balanceHeading}</p>
            {primary.scopeBadgeLabel && (
              <p
                className={styles.scopeBadge}
                style={{ background: badgeColor(primary) }}
              >
                {primary.scopeBadgeLabel}
              </p>
            )}
          </div>

          <BalanceBox primary={primary} familyView={familyView} />
          <ServiceGroups
            groups={groups}
            openGroupIndex={openGroupIndex}
            onToggle={toggleGroup}
            familyView={familyView}
          />
        </div>
      </div>
    </div>
  );
}

export default WalletBalanceSection;
